import React, { useMemo, useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import { Box, Button, Typography, IconButton, Paper, Switch, FormControlLabel, TextField, Tooltip, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, MenuItem, useMediaQuery } from "@mui/material";
import { MobileNumberField } from './MobileNumberField';
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Expense } from "../utils/interface";
import { TextInputCell } from "./TextInputCell";
import { NumberInputCell } from "./NumberInputCell";
import { HEADER_FOOTER_HEIGHT, ROW_HEIGHT } from "../utils/constants";
import { NumberDecimalInputCell } from "./NumberDecimalInputCell";
import { getOperatingExpenseAnnualByName } from "../utils/operatingExpenseCalc";
import { ExpensesSuggested, ExpensesBasic, ExpensesIndustrial, ExpensesSuggestedDevelopment, ExpensesBasicDevelopment} from "../utils/newModelConstants";
import { generateRowId, CommitGuard } from "../utils/rowAddGuard";
import { parseNumericField, sumByType } from "../utils/projectCostCalcs";
import { colors, typography } from "../theme";

// Grid sizing. The chrome allowance covers the 50px column header plus the
// footer (16px padding top and bottom around a small control row) and leaves
// room for a horizontal scrollbar; being a little generous costs a sliver of
// white, while being short would squeeze the rows area for no reason.
const EXPENSE_ROW_HEIGHT = 50;
const EXPENSE_GRID_CHROME_HEIGHT = 140;
const MAX_EXPENSE_GRID_HEIGHT = 640;

/**
 * Total annual Hard Costs INCLUDING the contingency line ("Total percent of other
 * expenses"). This is the basis the "Percent of Hard Costs" factor multiplies against,
 * and it must match the Hard Costs total shown in Sources & Uses. Previously the
 * contingency row was silently dropped, understating the basis (e.g. $6.6mm instead of
 * the $7.0mm S&U figure).
 */
const computeHardCostsAnnual = (expenses: any[], buildableSqFt: number): number =>
  sumByType(expenses || [], "Hard Costs", {
    acquisitionPrice: 0,
    buildableSqFt: Number(buildableSqFt || 0),
    constructionLoanAmount: 0,
    prefMezzAmount: 0,
    hardCostsTotal: 0,
    allExpenses: expenses || [],
  });

const EditableNameCell = React.memo(function EditableNameCell({
  initialValue,
  onCommit,
  onCancel,
}: {
  initialValue: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [localValue, setLocalValue] = useState<string>(initialValue ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Focus on mount
    const el = inputRef.current;
    if (el) {
      try {
        el.focus();
        // keep caret where user clicked; default to end
        const end = el.value.length;
        el.setSelectionRange(end, end);
      } catch {}
    }
  }, []);

  return (
    <TextField
      value={localValue}
      onChange={(e) => {
        // purely local to avoid parent re-render cursor jumps
        setLocalValue(e.target.value);
      }}
      variant="standard"
      size="small"
      fullWidth
      inputRef={inputRef}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Prevent DataGrid from hijacking typing/navigation
        e.stopPropagation();
        if (e.key === "Enter") {
          onCommit(localValue);
        } else if (e.key === "Escape") {
          onCancel();
        }
      }}
      onBlur={() => {
        onCommit(localValue);
      }}
      sx={{
        minWidth: 0,
        width: '100%',
        '& .MuiInputBase-input': {
          padding: 0,
          textAlign: 'left',
          height: '100%',
          fontSize: 14.5,
          fontWeight: 400,
          fontFamily: typography.fontFamily
        }
      }}
      InputProps={{
        disableUnderline: true,
        sx: { px: 0, py: 0, height: 36, display: 'flex', alignItems: 'center', background: 'transparent', fontFamily: 'inherit' }
      }}
      inputProps={{ maxLength: 100 }}
    />
  );
});

const EXPENSE_STEPS_MAPPING = {
  "Closing Costs": "Closing Costs",
  "Legal and Pre-Development Costs": "Legal and Pre-Development Costs",
  "Reserves": "Reserves",
  "Hard Costs": "Hard Costs",
  "Soft Costs": "Soft Costs",
};


const CustomFooter = ({
  expenses,
  expenseType,
  modelDetails,
  variables,
  operatingExpenses,
  units,
  amenityIncome,
  retailIncome,
  retailExpenses,
  onAdd,
  onAddWithName,
  showMonths,
  setShowMonths,
  buildableSqFt,
  showBuildableSQFt,
  isDevelopmentModel,
}: {
  expenses: Expense[];
  expenseType: string;
  modelDetails: any;
  variables: any;
  operatingExpenses: any[];
  units: any[];
  amenityIncome: any[];
  retailIncome: any[];
  retailExpenses: any[];
  onAdd: () => void;
  onAddWithName: (name: string) => void;
  showMonths: boolean;
  setShowMonths: (value: boolean) => void;
  buildableSqFt?: number;
  showBuildableSQFt?: boolean;
  isDevelopmentModel?: boolean;
}) => {
  // Filter expenses for this specific type
  const filteredExpenses = expenses.filter(exp => exp.type === expenseType);
  const newExpenseInputRef = useRef<HTMLInputElement | null>(null);
  const suggestedOptions = useMemo(() => {
    const existing = new Set(
      filteredExpenses.map(e => (e.name || '').trim().toLowerCase())
    );
    // Merge Suggested + Basic, filter by current type, dedupe by lowercase name
    const mergedNames: string[] = [];
    const seen = new Set<string>();
    const source = isDevelopmentModel ? [...ExpensesSuggestedDevelopment, ...ExpensesBasicDevelopment] : [...ExpensesSuggested, ...ExpensesBasic];
    for (const item of source) {
      const name = (item?.name || '').trim();
      const key = name.toLowerCase();
      if (name && !seen.has(key)) {
        seen.add(key);
        mergedNames.push(name);
      }
    }
    // Exclude ones already added in this table
    return mergedNames.filter(n => !existing.has(n.trim().toLowerCase()));
  }, [expenses, expenseType]);
  
  // Calculate total monthly and annual expenses from rows
  const computedRows = filteredExpenses.map(row => {
    let monthly = 0;
    let annual = 0;
    
    if (row.factor && row.factor.toLowerCase() === "Total".toLowerCase()) {
      // For total expenses, divide by the number of months in the period
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round(((row.cost_per || 0) / monthsInPeriod) * 100) / 100;
      annual = row.cost_per || 0;
    } else if (row.factor && row.factor.toLowerCase() === "per Unit".toLowerCase() || row.factor && row.factor.toLowerCase() === "per SF".toLowerCase() || row.factor && row.factor.toLowerCase() === "per Month".toLowerCase()) {
      // For per-unit, per-SF, or per-month expenses
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      const totalCost = Number(row.cost_per || 0) * Number(row.statistic || 0);
      monthly = Math.round((totalCost / monthsInPeriod) * 100) / 100;
      annual = totalCost;
    } else if (row.factor && row.factor.toLowerCase() === "$ / buildable sf".toLowerCase()) {
      // For buildable SF factor, use provided buildableSqFt from props (read-only statistic)
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      const sf = Number(buildableSqFt || 0);
      const totalCost = Number(row.cost_per || 0) * sf;
      monthly = Math.round((totalCost / monthsInPeriod) * 100) / 100;
      annual = totalCost;
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Purchase Price".toLowerCase()) {
      const acquisitionPriceField = modelDetails?.user_model_field_values?.find(
        (field: any) => field.field_key && field.field_key.trim() === "Acquisition Price"
      );
      const acquisitionPrice = parseNumericField(acquisitionPriceField?.value);
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round((Number(row.cost_per)/100 * Number(acquisitionPrice) / monthsInPeriod) * 100) / 100;
      annual = Number(row.cost_per)/100 * Number(acquisitionPrice);
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Acquisition Loan".toLowerCase()) {
      const acquisitionLoanRaw = variables?.["AQ: Max Acquisition Loan at Closing"];
                  const acquisitionLoan = Number(typeof acquisitionLoanRaw === "string" ? acquisitionLoanRaw.replace(/,/g, '').trim() : acquisitionLoanRaw ?? 0);
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round((Number(row.cost_per)/100 * Number(acquisitionLoan) / monthsInPeriod) * 100) / 100;
      annual = Number(row.cost_per)/100 * Number(acquisitionLoan);
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Construction Loan".toLowerCase()) {
      const candidates = [
        variables?.["Sr. Cons: Exact Loan Amount"],
      ];
      let cl = 0;
      for (const c of candidates) {
        const n = typeof c === 'string' ? Number(String(c).replace(/,/g, '').trim()) : Number(c ?? 0);
        if (Number.isFinite(n) && n) { cl = n; break; }
      }
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round(((Number(row.cost_per)/100 * cl) / monthsInPeriod) * 100) / 100;
      annual = (Number(row.cost_per)/100) * cl;
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Pref / Mezz Loan".toLowerCase()) {
      const prefMezzLoanField = modelDetails?.user_model_field_values?.find(
        (field: any) => field.field_key && field.field_key.trim() === "Pref. Equity / Mezz. Loan Amount"
      );
      const candidates = [
        prefMezzLoanField?.value,
        variables?.["Pref. / Mezz: Loan Amount"],
      ];
      let pm = 0;
      for (const c of candidates) {
        const n = typeof c === 'string' ? Number(String(c).replace(/,/g, '').trim()) : Number(c ?? 0);
        if (Number.isFinite(n) && n) { pm = n; break; }
      }
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round(((Number(row.cost_per)/100 * pm) / monthsInPeriod) * 100) / 100;
      annual = (Number(row.cost_per)/100) * pm;
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Hard Costs".toLowerCase()) {
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      const hardCostsAnnual = computeHardCostsAnnual(expenses, Number(buildableSqFt || 0));
      monthly = Math.round((((Number(row.cost_per) / 100) * hardCostsAnnual) / monthsInPeriod) * 100) / 100;
      annual = (Number(row.cost_per) / 100) * hardCostsAnnual;
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Property Taxes".toLowerCase()) {
      const taxesAnnual = getOperatingExpenseAnnualByName({
        expenseName: "Property Taxes",
        operatingExpenses: operatingExpenses || [],
        modelDetails,
        units: units || [],
        amenityIncome: amenityIncome || [],
        retailIncome: retailIncome || [],
        retailExpenses: retailExpenses || [],
      });
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round(((Number(row.cost_per) / 100) * taxesAnnual / monthsInPeriod) * 100) / 100;
      annual = (Number(row.cost_per) / 100) * taxesAnnual;
    } else if (row.factor && row.factor.toLowerCase() === "Percent of Insurance Cost".toLowerCase()) {
      const insuranceAnnual = getOperatingExpenseAnnualByName({
        expenseName: "Insurance",
        operatingExpenses: operatingExpenses,
        modelDetails,
        units: units,
        amenityIncome: amenityIncome,
        retailIncome: retailIncome,
        retailExpenses: retailExpenses,
      });

      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round(((Number(row.cost_per) / 100) * insuranceAnnual / monthsInPeriod) * 100) / 100;
      annual = (Number(row.cost_per) / 100) * insuranceAnnual;
    }
    else if ((row.factor || "").toLowerCase().includes("percent of other")) {
      // Calculate the sum of other expenses for this type
      const buildableSf = Number(buildableSqFt ?? 0);
      const otherTotals = filteredExpenses
        .filter((exp) => exp.id !== row.id)
        .map((exp) => {
          const f = (exp.factor || "").trim().toLowerCase();
          if (f === "total") return Number(exp.cost_per) ?? 0;
          if (f.includes("buildable") && f.includes("sf")) return Number(exp.cost_per || 0) * buildableSf;
          if (f === "per unit" || f === "per sf" || f === "per month") {
            return Number(exp.cost_per || 0) * Number(exp.statistic || 0);
          }
          if (f === "percent of purchase price") {
            const acquisitionPriceField = modelDetails?.user_model_field_values?.find(
              (field: any) => field.field_key && field.field_key.trim() === "Acquisition Price"
            );
            const acquisitionPrice = parseNumericField(acquisitionPriceField?.value);
            return Number(exp.cost_per)/100 * Number(acquisitionPrice);
          }
          if (exp.factor && exp.factor.toLowerCase() === "Percent of Acquisition Loan".toLowerCase()) {
            const acquisitionLoanRaw = variables?.["AQ: Max Acquisition Loan at Closing"];
                  const acquisitionLoan = Number(typeof acquisitionLoanRaw === "string" ? acquisitionLoanRaw.replace(/,/g, '').trim() : acquisitionLoanRaw ?? 0);
            return Number(exp.cost_per)/100 * Number(acquisitionLoan);
          }
          if (exp.factor && exp.factor.toLowerCase() === "Percent of Property Taxes".toLowerCase()) {
            const taxesAnnual = getOperatingExpenseAnnualByName({
              expenseName: "Property Taxes",
              operatingExpenses: operatingExpenses,
              modelDetails,
              units: units,
              amenityIncome: amenityIncome,
              retailIncome: retailIncome,
              retailExpenses: retailExpenses,
            });
            return (Number(exp.cost_per) / 100) * taxesAnnual;
          }
          if (exp.factor && exp.factor.toLowerCase() === "Percent of Insurance Cost".toLowerCase()) {
            const insuranceAnnual = getOperatingExpenseAnnualByName({
              expenseName: "Insurance",
              operatingExpenses: operatingExpenses,
              modelDetails,
              units: units,
              amenityIncome: amenityIncome,
              retailIncome: retailIncome,
              retailExpenses: retailExpenses,
            });

            return (Number(exp.cost_per) / 100) * insuranceAnnual;
          }
          return 0;
        })
        .reduce((sum, val) => (sum ?? 0) + Number(val), 0);
      
      const totalCost = Number(row.cost_per || 0) * Number(otherTotals) * 0.01;
      const monthsInPeriod = (row.end_month || 12) - (row.start_month || 1) + 1;
      monthly = Math.round((totalCost / monthsInPeriod) * 100) / 100;
      annual = totalCost;
    }
    
    return { ...row, monthly, annual };
  });
  
  const totalMonthlyExpenses = computedRows.reduce((sum, row) => sum + (row.monthly || 0), 0);
  const totalAnnualExpenses = Math.ceil(computedRows.reduce((sum, row) => sum + (row.annual || 0), 0));

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: colors.white,
        borderTop: `1px solid ${colors.grey[300]}`,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 1, width: { xs: '100%', md: 'auto' } }}>
        <Autocomplete
          freeSolo
          size="small"
          fullWidth
          options={suggestedOptions}
          // Always-null controlled value: prevents MUI from retaining a
          // previously-committed value that could re-fire onChange on blur
          // and cause a duplicate row (CEO video May 2026).
          value={null}
          clearOnBlur
          // Keep input uncontrolled to avoid focus loss while typing
          onChange={(_e, v) => {
            const name = typeof v === 'string' ? v : (v as any) || '';
            const trimmed = String(name).trim();
            if (trimmed) {
              onAddWithName(trimmed);
              if (newExpenseInputRef.current) newExpenseInputRef.current.value = '';
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Add or select ${expenseType.endsWith('s') ? expenseType.slice(0, -1) : expenseType}`}
              placeholder="e.g., Application Fee"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const currentVal = (e.target as HTMLInputElement)?.value ?? '';
                  const trimmed = currentVal.trim();
                  if (trimmed) {
                    e.preventDefault();
                    onAddWithName(trimmed);
                    if (newExpenseInputRef.current) newExpenseInputRef.current.value = '';
                  }
                }
              }}
              inputRef={newExpenseInputRef}
              sx={{
                minWidth: { xs: 0, md: 260 },
                '& .MuiInputBase-input': { fontSize: 14, fontFamily: 'inherit' },
                '& .MuiInputLabel-root': { fontSize: 14, fontFamily: 'inherit' },
                '& .MuiInputLabel-shrink': { fontSize: 14, fontFamily: 'inherit' }
              }}
            />
          )}
        />
        <Button variant="contained" size="small" onClick={() => {
          const currentVal = newExpenseInputRef.current?.value ?? '';
          const trimmed = currentVal.trim();
          if (trimmed) {
            onAddWithName(trimmed);
            if (newExpenseInputRef.current) newExpenseInputRef.current.value = '';
          } else {
            onAdd();
          }
        }} sx={{ whiteSpace: 'nowrap', minWidth: { xs: 0, md: 220 }, width: { xs: '100%', md: 'auto' }, fontSize: 14, fontWeight: 600 }}>
          Add {expenseType === 'Legal and Pre-Development Costs' ? 'Legal or Setup Cost' : (expenseType.endsWith('s') ? expenseType.slice(0, -1) : expenseType)}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 1, md: 2 }, marginLeft: { md: 'auto' }, width: { xs: '100%', md: 'auto' } }}>
        <FormControlLabel
          control={<Switch checked={showMonths} onChange={(e) => setShowMonths(e.target.checked)} />}
          label={showMonths ? 'Hide Start/End Months' : 'Show Start/End Months'}
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14, fontFamily: 'inherit', fontWeight: 500, color: colors.grey[700] } }}
        />
        {showBuildableSQFt ? (
          <Box sx={{ textAlign: 'right', color: colors.grey[700], fontWeight: 600, fontSize: 14 }}>
            Buildable SF: {Number(buildableSqFt || 0).toLocaleString()}
          </Box>
        ) : null}
        <Box sx={{ textAlign: 'right', color: colors.grey[700], fontWeight: 600, fontSize: 14 }}>
          Total {expenseType}: ${totalAnnualExpenses.toLocaleString()}
        </Box>
      </Box>
    </Box>
  );
};


export const Expenses = ({
  operatingExpenses,
  expenses,
  setExpenses,
  step,
  modelDetails,
  variables,
  units,
  amenityIncome,
  retailIncome,
  retailExpenses,
  industrialModel,
  defaultShowMonths,
  showBuildableSQFt,
  buildableSQFt,
  modelType
}: {
  operatingExpenses: any[];
  expenses: Expense[];
  setExpenses: React.Dispatch<React.SetStateAction<Expense[]>>;
  step: string;
  modelDetails: any;
  variables: any;
  units: any[];
  amenityIncome: any[];
  retailIncome: any[];
  retailExpenses: any[];
  industrialModel?: boolean;
  defaultShowMonths: boolean;
  showBuildableSQFt?: boolean;
  buildableSQFt?: number;
  modelType: any;
}) => {
  const expenseType = EXPENSE_STEPS_MAPPING[step as keyof typeof EXPENSE_STEPS_MAPPING];
  const prevExpensesRef = useRef(expenses);
  const [showMonths, setShowMonths] = useState<boolean>(defaultShowMonths);
  const [editingNameRowId, setEditingNameRowId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState<string>('');
  const isDevelopmentModel = Boolean(modelType?.development_model);

  // Only show expenses for this type
  const filteredExpenses = expenses.filter(exp => exp.type === expenseType);

  // Always render "Total percent of other expenses" rows at the bottom
  const orderedFilteredExpenses = useMemo(() => {
    const isTotalPercent = (row: Expense) =>
      typeof row?.factor === 'string' &&
      row.factor.toLowerCase() === 'total percent of other expenses'.toLowerCase();
    const others: Expense[] = [];
    const totals: Expense[] = [];
    for (const row of filteredExpenses) {
      if (isTotalPercent(row)) totals.push(row);
      else others.push(row);
    }
    return [...others, ...totals];
  }, [filteredExpenses]);


const handleCellChange = (id: string, field: string, value: string | number) => {

  // Remove debug logs for production
  setExpenses(prev =>
    prev.map(row => {
      if (row.id !== id) return row;

      // If changing "factor"
      if (field === "factor") {
        if (value && typeof value === "string" && value.toLowerCase() === "Total".toLowerCase()) {
          return { ...row, factor: value as string, cost_per: 0, statistic: null };
        }
        if (value && typeof value === "string" && value.toLowerCase() === "Total percent of other expenses".toLowerCase()) {
          return { ...row, factor: value as string, cost_per: 0, statistic: 0 };
        }
        // Any other factor
        return { ...row, factor: value as string, cost_per: 0, statistic: 0 };
      }

      // Otherwise, update as normal
      return { ...row, [field]: value };
    })
  );
}


  // Update cost_per for "Total percent of other expenses" rows when other expenses change
  useEffect(() => {
    const prevExpenses = prevExpensesRef.current;
    const updatedExpenses = expenses.map(exp => {
      // Remove automatic cost_per calculation - user enters percentage manually
      return exp;
    });
    
    // Only update if there are actual changes to avoid infinite loops
    const hasChanges = updatedExpenses.some((exp, index) => {
      const prevExp = prevExpenses[index];
      return !prevExp || exp.cost_per !== prevExp.cost_per;
    });
    
    if (hasChanges) {
      setExpenses(updatedExpenses);
    }
    
    prevExpensesRef.current = expenses;
  }, [expenses, expenseType, setExpenses, variables]);

  const totalPercentUsed = filteredExpenses.some(
    (exp) => exp.factor && exp.factor.toLowerCase() === "Total percent of other expenses".toLowerCase()
  );

  // Function to determine if a cell is editable
  const isCellEditable = (field: string, row: Expense) => {
    if (field === "name" || field === "factor" || field === "start_month" || field === "end_month") {
      return true;
    }
    if (field === "cost_per") {
      return true
      // return row.factor !== "Total percent of other expenses";
    }
    if (field === "statistic") {
      const factor = (row.factor ?? "").toLowerCase();
      return factor !== "total" && factor !== "total percent of other expenses" && factor !== "$ / buildable sf";
    }
    if (field === "total_cost") {
      return false; // Always read-only
    }
    return true;
  };

  const flexByField: Record<string, number> = {
    name: 1.6,
    factor: 1.5,
    cost_per: 1.5,
    statistic: 1,
    total_cost: 1,
    start_month: 0.9,
    end_month: 0.9,
    delete: 0.4,
  };

  const allColumns = useMemo<GridColDef<Expense>[]>(() => [
    { field: "name", headerName: "Name", editable: false, flex: flexByField.name, minWidth: 160,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 14.5, color: 'text.primary', fontFamily: typography.fontFamily }}>Name</Typography>
          <Tooltip title={`Add ${expenseType.endsWith('s') ? expenseType.slice(0, -1) : expenseType}`} arrow>
            <IconButton size="small" onClick={addRow} sx={{ p: 0.25 }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      renderCell: (params: any) => (
        editingNameRowId === String(params.id) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <EditableNameCell
              initialValue={editingNameValue}
              onCommit={(val) => {
                const trimmed = val.trim();
                handleCellChange(String(params.id), 'name', trimmed);
                setEditingNameRowId(null);
              }}
              onCancel={() => {
                  setEditingNameRowId(null);
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
            <span title={typeof params.value === 'string' ? params.value : undefined} style={{ color: '#1f2937', fontSize: 14.5, fontFamily: typography.fontFamily, fontWeight: 400, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.value || <span style={{ color: '#aaa', fontFamily: typography.fontFamily }}>Untitled</span>}
            </span>
            <span className="u-row-action" style={{ flexShrink: 0 }}>
              <Tooltip title="Edit name" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingNameRowId(String(params.id));
                    setEditingNameValue(String(params.value || ''));
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ p: 0.25 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          </Box>
        )
      )
    },
    {
      field: "factor",
      headerName: "Factor",
      flex: flexByField.factor,
      minWidth: 150,
      editable: false,
      renderCell: (params: any) => {

        // Total
        // per Unit
        // per SF
        // per Month
        // Percent of Purchase Price
        // Percent of Acquisition Loan
        // Total percent of other expenses
        
        // Only hide "Total percent of other expenses" if it is actually selected in another row
        let options: { value: string; label: string }[] = [
          { value: "Total", label: "Total" },
          { value: "per Unit", label: "per Unit" },
          { value: "per SF", label: "per SF" },
          { value: "per Month", label: "per Month" },
        ];
        // Determine this row's type from data (avoids relying on outer expenseType)
        const thisType = params.row.type as string;
    
        if (isDevelopmentModel) {
          if (thisType === "Closing Costs") {
         
            options = [
              { value: "Percent of Purchase Price", label: "Percent of Purchase Price" },
              { value: "Percent of Construction Loan", label: "Percent of Construction Loan" },
              { value: "Percent of Pref / Mezz Loan", label: "Percent of Pref / Mezz Loan" },
              { value: "per Unit", label: "per Unit" },
              { value: "per SF", label: "per SF" },
              { value: "Total", label: "Total" },
            ];
          } else if (thisType === "Legal and Pre-Development Costs") {
            options = [
              { value: "Percent of Construction Loan", label: "Percent of Construction Loan" },
              { value: "Percent of Pref / Mezz Loan", label: "Percent of Pref / Mezz Loan" },
              { value: "per Unit", label: "per Unit" },
              { value: "per SF", label: "per SF" },
              { value: "per Month", label: "per Month" },
              { value: "Total", label: "Total" },
            ];
          } else if (thisType === "Soft Costs") {
            options = [
              { value: "Percent of Hard Costs", label: "Percent of Hard Costs" },
              { value: "per Unit", label: "per Unit" },
              { value: "per SF", label: "per SF" },
              { value: "per Month", label: "per Month" },
              { value: "Total", label: "Total" },

            ];
          } else if (thisType === "Hard Costs") {
            options = [
              { value: "per Unit", label: "per Unit" },
              { value: "$ / buildable SF", label: "$ / buildable SF" },
              { value: "per SF", label: "per SF" },
              { value: "per Month", label: "per Month" },
              { value: "Total", label: "Total" },
            ];
          }
        } else {
          if (thisType === "Closing Costs") {
            options.push({
              value: "Percent of Purchase Price",
              label: "Percent of Purchase Price"
            });
            options.push({
              value: "Percent of Acquisition Loan",
              label: "Percent of Acquisition Loan"
            });

            if (!industrialModel) {
              options.push({
                value: "Percent of Property Taxes",
                label: "Percent of Property Taxes"
              });
              options.push({
                value: "Percent of Insurance Cost",
                label: "Percent of Insurance Cost"
              });
            }
          }
          // Add $ / buildable SF only for Hard Costs when buildable SF context is available
          if (thisType === "Hard Costs" && showBuildableSQFt) {
            options.push({
              value: "$ / buildable SF",
              label: "$ / buildable SF"
            });
          }
        }
        // Recompute rows limited to this type for the option gating to avoid cross-influence
        const rowsOfThisType = expenses.filter((exp) => exp.type === thisType);
        if (!rowsOfThisType.some((exp) => exp.factor === "Total percent of other expenses" && exp.id !== params.row.id)) {
          options.push({
            value: "Total percent of other expenses",
            label: "Percent of other expense total",
          });
        }

        // Find the option whose value matches params.value case-insensitively
        const selectedOption = options.find(
          (option) => option.value.toLowerCase() === String(params.value).toLowerCase()
        );
        // If found, use its value (preserving case) as the select value, else fallback to params.value
        const selectValue = selectedOption ? selectedOption.value : params.value;

        return (
          <div className="u-select-wrap" style={{ position: 'relative', width: '100%' }}>
            <select
              value={selectValue}
              onChange={(e) => handleCellChange(String(params.id), "factor", e.target.value)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                borderRadius: 4,
                padding: '10px 28px 10px 8px',
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                fontFamily: typography.fontFamily,
                fontSize: 14.5,
                fontWeight: 600,
                color: '#1f2937'
              }}
              className="u-select"
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="u-caret" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>▾</span>
          </div>
        );
      },
    },
    {
      field: "cost_per",
      headerName: "Cost Per",
      flex: flexByField.cost_per,
      // Wide enough that the value still shows next to the longest unit label
      // ("/ buildable sf"), which reserves ~88px of the cell.
      minWidth: 175,
      editable: false,
      type: "number",
      renderCell: (params: any) => {
        let adornment = "";
        let preAdornment = "";
        const editable = isCellEditable("cost_per", params.row);
        switch (params.row.factor.toLowerCase()) {
          case "per Unit".toLowerCase():
            preAdornment = "$";
            adornment = "/ unit";
            break;
          case "per SF".toLowerCase():
            preAdornment = "$";
            adornment = "/ sf";
            break;
          case "$ / buildable sf".toLowerCase():
            preAdornment = "$";
            adornment = "/ buildable sf";
            break;
          case "per Month".toLowerCase():
            preAdornment = "$";
            adornment = "/ month";
            break;
          case "Total percent of other expenses".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Purchase Price".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Insurance Cost".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Property Taxes".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Acquisition Loan".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Construction Loan".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Pref / Mezz Loan".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Percent of Hard Costs".toLowerCase():
            preAdornment = "";
            adornment = "%";
            break;
          case "Total".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          default:
            preAdornment = "";
            adornment = "";
        }
        if (editable) {
          return (
            <NumberDecimalInputCell params={params} handleCellChange={handleCellChange} field="cost_per" prefix={preAdornment} suffix={adornment} />
          )
        }else{
          const formattedAdornment = adornment && adornment.startsWith('/') ? ` ${adornment}` : adornment;
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              {preAdornment && <span style={{ color: "#888", marginRight: 4 }}>{preAdornment}</span>}
              {params.value}
              {formattedAdornment && <span style={{ color: "#888", marginLeft: 6 }}>{formattedAdornment}</span>}
            </span>
          );
        }

      },
    },
    {
      field: "statistic",
      headerName: "Statistic",
      flex: flexByField.statistic,
      minWidth: 130,
      editable: false,
      type: "number",
      renderCell: (params: any) => {
        let adornment = "";
        let preAdornment = "";
        const editable = isCellEditable("statistic", params.row);
        
        switch (params.row.factor.toLowerCase()) {
          case "per Unit".toLowerCase():
            preAdornment = "";
            adornment = "units";
            break;
          case "per SF".toLowerCase():
            preAdornment = "";
            adornment = "sf";
            break;
          case "$ / buildable sf".toLowerCase():
            preAdornment = "";
            adornment = "sf";
            break;
          case "per Month".toLowerCase():
            preAdornment = "";
            adornment = "months";
            break;
          case "Total percent of other expenses".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Purchase Price".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Acquisition Loan".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Construction Loan".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Pref / Mezz Loan".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Hard Costs".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Property Taxes".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          case "Percent of Insurance Cost".toLowerCase():
            preAdornment = "$";
            adornment = "";
            break;
          default:
            preAdornment = "";
            adornment = "";
        }

        // If factor is "Total percent of other expenses", show sum of other expenses
        const rowFactor = (params.row.factor || "").trim().toLowerCase();
        const isTotalPercentOfOther = rowFactor === "total percent of other expenses" || rowFactor.includes("percent of other");
        if (isTotalPercentOfOther) {
          const { id, type } = params.row;
          const buildableSf = Number(buildableSQFt ?? 0);
          const otherTotals = expenses
            .filter((exp) => exp.type === type && exp.id !== id)
            .map((exp) => {
              const f = (exp.factor || "").trim().toLowerCase();
              if (f === "total") return Number(exp.cost_per) ?? 0;
              // $ / buildable SF (any casing/spacing) – use same buildable SF as the column uses for those rows
              if (f.includes("buildable") && f.includes("sf")) {
                return Number(exp.cost_per) * buildableSf;
              }
              if (exp.factor && exp.factor.toLowerCase() === "Percent of Purchase Price".toLowerCase()) {
                const acquisitionPriceField = modelDetails?.user_model_field_values?.find(
                  (field: any) => field.field_key && field.field_key.trim() === "Acquisition Price"
                );
                const acquisitionPrice = parseNumericField(acquisitionPriceField?.value);
                return Number(exp.cost_per)/100 * Number(acquisitionPrice);
              }
              if (exp.factor && exp.factor.toLowerCase() === "Percent of Acquisition Loan".toLowerCase()) {
                const rawLoan = variables?.["AQ: Max Acquisition Loan at Closing"];
                const acquisitionLoan = Number(typeof rawLoan === "string" ? rawLoan.replace(/,/g, '').trim() : rawLoan || 0);
                return Number(exp.cost_per)/100 * Number(acquisitionLoan);
              }
              if (exp.factor && exp.factor.toLowerCase() === "Percent of Property Taxes".toLowerCase()) {
                const taxesAnnual = getOperatingExpenseAnnualByName({
                  expenseName: "Property Taxes",
                  operatingExpenses: operatingExpenses || [],
                  modelDetails,
                  units: units,
                  amenityIncome: amenityIncome,
                  retailIncome: retailIncome,
                  retailExpenses: retailExpenses,
                });
                return (Number(exp.cost_per) / 100) * taxesAnnual;
              }
              if (exp.factor && exp.factor.toLowerCase() === "Percent of Insurance Cost".toLowerCase()) {
                const insuranceAnnual = getOperatingExpenseAnnualByName({
                  expenseName: "Insurance",
                  operatingExpenses: operatingExpenses || [],
                  modelDetails,
                  units: units,
                  amenityIncome: amenityIncome,
                  retailIncome: retailIncome,
                  retailExpenses: retailExpenses,
                });
                return (Number(exp.cost_per) / 100) * insuranceAnnual;
              }
              if (
                exp.factor && exp.factor.toLowerCase() === "per Unit".toLowerCase() ||
                exp.factor && exp.factor.toLowerCase() === "per SF".toLowerCase() ||
                exp.factor && exp.factor.toLowerCase() === "per Month".toLowerCase()
              ) {
                return Number(exp.cost_per) * Number(exp.statistic);
              }
              return 0;
            })
            .reduce((sum, val) => (sum ?? 0) + Number(val), 0);
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              {preAdornment && <span style={{ color: "#888", marginRight: 4 }}>{preAdornment}</span>}
              {(otherTotals ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              <span style={{ color: "#888", marginLeft: 6 }}>{adornment}</span>
            </span>
          );
        }
        // Show read-only buildable SF when using "$ / buildable SF"
        if ((params.row.factor || "").trim().toLowerCase().includes("buildable")) {
          const sf = Number(buildableSQFt || 0);
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              {preAdornment && <span style={{ color: "#888", marginRight: 4 }}>{preAdornment}</span>}
              {sf.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              <span style={{ color: "#888", marginLeft: 6 }}>{adornment}</span>
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Purchase Price".toLowerCase()) {
          const acquisitionPriceField = modelDetails?.user_model_field_values?.find(
            (field: any) => field.field_key && field.field_key.trim() === "Acquisition Price"
          );
          const acquisitionPrice = parseNumericField(acquisitionPriceField?.value);

          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              ${acquisitionPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Acquisition Loan".toLowerCase()) {
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              {variables?.["AQ: Max Acquisition Loan at Closing"]
                ? `$${typeof variables["AQ: Max Acquisition Loan at Closing"] === "string"
                    ? variables["AQ: Max Acquisition Loan at Closing"].trim()
                    : (variables["AQ: Max Acquisition Loan at Closing"] as number).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : "N/A"}
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Construction Loan".toLowerCase()) {
          const candidates = [
            variables?.["Sr. Cons: Exact Loan Amount"],
            variables?.["CN: Max Construction Loan"],
            variables?.["Construction Loan Amount"],
            variables?.["Max Construction Loan"],
            variables?.["Construction Loan"],
          ];
          const amt = (() => {
            for (const c of candidates) {
              const n = typeof c === 'string' ? Number(c.replace(/,/g, '').trim()) : Number(c || 0);
              if (Number.isFinite(n) && n) return n;
            }
            return 0;
          })();
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              ${amt.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Pref / Mezz Loan".toLowerCase()) {
          const prefMezzLoanField = modelDetails?.user_model_field_values?.find(
            (field: any) => field.field_key && field.field_key.trim() === "Pref. Equity / Mezz. Loan Amount"
          );
          const candidates = [
            prefMezzLoanField?.value,
            variables?.["Pref. / Mezz: Loan Amount"],

          ];
          const amt = (() => {
            for (const c of candidates) {
              const n = typeof c === 'string' ? Number(c.replace(/,/g, '').trim()) : Number(c || 0);
              if (Number.isFinite(n) && n) return n;
            }
            return 0;
          })();
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              ${amt.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Hard Costs".toLowerCase()) {
          const hardCostsAnnual = computeHardCostsAnnual(expenses, Number(buildableSQFt || 0));
          // Persist computed statistic on the row to mirror footer behavior
          try {
            if (typeof params.row.statistic !== 'number' || Number(params.row.statistic) !== Number(hardCostsAnnual)) {
              (params.row as any).statistic = Number(hardCostsAnnual);
            }
          } catch {}
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              ${hardCostsAnnual.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Property Taxes".toLowerCase()) {
          const taxesAnnual = getOperatingExpenseAnnualByName({
            expenseName: "Property Taxes",
            operatingExpenses: operatingExpenses || [],
            modelDetails,
            units: units,
            amenityIncome: amenityIncome,
            retailIncome: retailIncome,
            retailExpenses: retailExpenses,
          });
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              ${taxesAnnual.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          );
        }
        else if (params.row.factor.toLowerCase() === "Percent of Insurance Cost".toLowerCase()) {
          const insuranceAnnual = getOperatingExpenseAnnualByName({
            expenseName: "Insurance",
            operatingExpenses: operatingExpenses || [],
            modelDetails,
            units: units,
            amenityIncome: amenityIncome,
            retailIncome: retailIncome,
            retailExpenses: retailExpenses,
          });
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              ${insuranceAnnual.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          );
        }
        if (editable) {
          return (
            <NumberInputCell params={params} handleCellChange={handleCellChange} field="statistic" suffix={adornment} />
          )
        }else{
          const formattedAdornment = adornment ? ` ${adornment}` : '';
          return (
            <span style={{ color: editable ? "inherit" : "#999" }}>
              {params.value}
              {formattedAdornment && <span style={{ color: "#888", marginLeft: 6 }}>{formattedAdornment}</span>}
            </span>
          );
        }

      },
    },
    {
      field: "total_cost",
      headerName: "Total Cost",
      flex: flexByField.total_cost,
      minWidth: 110,
      editable: false,
      type: "number",
      renderCell: (params: any) => {
        const { factor, cost_per, statistic, id, type } = params.row;
        let value = 0;
        if (factor === "Total") {
          value = cost_per;
        } else if (factor && factor.toLowerCase() === "per Unit".toLowerCase() || factor && factor.toLowerCase() === "per SF".toLowerCase() || factor && factor.toLowerCase() === "per Month".toLowerCase()) {
          value = Number(cost_per) * Number(statistic);
        } else if (factor && factor.toLowerCase() === "$ / buildable sf".toLowerCase()) {
          value = Number(cost_per) * Number(buildableSQFt || 0);
        } else if (factor == "Percent of Purchase Price"){
          const acquisitionPriceField = modelDetails?.user_model_field_values?.find(
            (field: any) => field.field_key && field.field_key.trim() === "Acquisition Price"
          );
          const acquisitionPrice = parseNumericField(acquisitionPriceField?.value);
          value = Number(cost_per)/100 * Number(acquisitionPrice);
        } else if (factor && factor.toLowerCase() === "Percent of Acquisition Loan".toLowerCase()) {
          const rawLoan = variables?.["AQ: Max Acquisition Loan at Closing"];
          const acquisitionLoan = Number(typeof rawLoan === "string" ? rawLoan.replace(/,/g, '').trim() : rawLoan || 0);
          value = Number(cost_per)/100 * Number(acquisitionLoan);
        } else if (factor && factor.toLowerCase() === "Percent of Construction Loan".toLowerCase()) {
          const cand = [
            variables?.["Sr. Cons: Exact Loan Amount"],
            variables?.["CN: Max Construction Loan"],
            variables?.["Construction Loan Amount"],
            variables?.["Max Construction Loan"],
            variables?.["Construction Loan"],
          ];
          let cl = 0;
          for (const c of cand) {
            const n = typeof c === 'string' ? Number(c.replace(/,/g, '').trim()) : Number(c || 0);
            if (Number.isFinite(n) && n) { cl = n; break; }
          }
          value = (Number(cost_per) / 100) * cl;
        } else if (factor && factor.toLowerCase() === "Percent of Pref / Mezz Loan".toLowerCase()) {
          const prefMezzLoanField = modelDetails?.user_model_field_values?.find(
            (field: any) => field.field_key && field.field_key.trim() === "Pref. Equity / Mezz. Loan Amount"
          );
          const cand = [
            prefMezzLoanField?.value,
            variables?.["Pref. / Mezz: Loan Amount"],
            variables?.["Max Pref / Mezz Loan"],
            variables?.["Pref / Mezz Loan Amount"],
            variables?.["Pref / Mezz Loan"],
          ];
          let pm = 0;
          for (const c of cand) {
            const n = typeof c === 'string' ? Number(c.replace(/,/g, '').trim()) : Number(c || 0);
            if (Number.isFinite(n) && n) { pm = n; break; }
          }
          value = (Number(cost_per) / 100) * pm;
        } else if (factor && factor.toLowerCase() === "Percent of Hard Costs".toLowerCase()) {
          const hardCostsAnnual = computeHardCostsAnnual(expenses, Number(buildableSQFt || 0));
          value = (Number(cost_per) / 100) * hardCostsAnnual;
        } else if (factor && factor.toLowerCase() === "Percent of Property Taxes".toLowerCase()) {
          const taxesAnnual = getOperatingExpenseAnnualByName({
            expenseName: "Property Taxes",
            operatingExpenses: operatingExpenses,
            modelDetails,
            units: units,
            amenityIncome: amenityIncome,
            retailIncome: retailIncome,
            retailExpenses: retailExpenses,
          });
          value = (Number(cost_per) / 100) * taxesAnnual;
        } else if (factor && factor.toLowerCase() === "Percent of Insurance Cost".toLowerCase()) {
          const insuranceAnnual = getOperatingExpenseAnnualByName({
            expenseName: "Insurance",
            operatingExpenses: operatingExpenses,
            modelDetails,
            units: units,
            amenityIncome: amenityIncome,
            retailIncome: retailIncome,
            retailExpenses: retailExpenses,
          });
          value = (Number(cost_per) / 100) * insuranceAnnual;
        }
        else if ((factor || "").trim().toLowerCase() === "total percent of other expenses" || (factor || "").toLowerCase().includes("percent of other")) {

            const { id, type } = params.row;
            const buildableSf = Number(buildableSQFt ?? 0);
            const otherTotals = expenses
                .filter((exp) => exp.type === type && exp.id !== id)
                .map((exp) => {
                const f = (exp.factor || "").trim().toLowerCase();
                if (f === "total") return Number(exp.cost_per) ?? 0;
                if (f.includes("buildable") && f.includes("sf")) return Number(exp.cost_per) * buildableSf;
                if (
                    f === "per unit" || f === "per sf" || f === "per month"
                ) {
                    return Number(exp.cost_per) * Number(exp.statistic);
                }
                if (f === "percent of purchase price") {
                  const acquisitionPriceField = modelDetails?.user_model_field_values?.find(
                    (field: any) => field.field_key && field.field_key.trim() === "Acquisition Price"
                  );
                  const acquisitionPrice = parseNumericField(acquisitionPriceField?.value);
                  return Number(exp.cost_per)/100 * Number(acquisitionPrice);
                }else if (exp.factor && exp.factor.toLowerCase() === "Percent of Acquisition Loan".toLowerCase()) {
                  const rawLoan = variables?.["AQ: Max Acquisition Loan at Closing"];
                  const acquisitionLoan = Number(typeof rawLoan === "string" ? rawLoan.replace(/,/g, '').trim() : rawLoan || 0);
                  return Number(exp.cost_per)/100 * Number(acquisitionLoan);
                }else if (exp.factor && exp.factor.toLowerCase() === "Percent of Property Taxes".toLowerCase()) {
                  const taxesAnnual = getOperatingExpenseAnnualByName({
                    expenseName: "Property Taxes",
                    operatingExpenses: operatingExpenses || [],
                    modelDetails,
                    units: units,
                    amenityIncome: amenityIncome,
                    retailIncome: retailIncome,
                    retailExpenses: retailExpenses,
                  });
                  return (Number(exp.cost_per) / 100) * taxesAnnual;
                }else if (exp.factor && exp.factor.toLowerCase() === "Percent of Insurance Cost".toLowerCase()) {
                  const insuranceAnnual = getOperatingExpenseAnnualByName({
                    expenseName: "Insurance",
                    operatingExpenses: operatingExpenses || [],
                    modelDetails,
                    units: units,
                    amenityIncome: amenityIncome,
                    retailIncome: retailIncome,
                    retailExpenses: retailExpenses,
                  });
                  return (Number(exp.cost_per) / 100) * insuranceAnnual;
                }else{
                  return 0;
                }
                
                })
                .reduce((sum, val) => (sum ?? 0) + Number(val), 0);
      
          // For "Total percent of other expenses", total cost is cost_per (percentage) * statistic (sum of other expenses) * 0.01
          value = Number(cost_per) * Number(otherTotals) * 0.01;
        }
        return (
          <span style={{ color: "#999" }}>
            ${value ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : 0}
          </span>
        );
      },
    },
    { field: "start_month", headerName: "Start Month", flex: flexByField.start_month, minWidth: 100, editable: false, type: "number",
      renderCell: (params: any) => {
        return <NumberInputCell prefix={"Month "} params={params} handleCellChange={handleCellChange} field="start_month" />
      }
    },
    { field: "end_month", headerName: "End Month", flex: flexByField.end_month, minWidth: 100, editable: false, type: "number",
      renderCell: (params: any) => {
        return <NumberInputCell prefix={"Month "} params={params} handleCellChange={handleCellChange} field="end_month" />
      }
    },
    {
      field: "delete",
      headerName: "",
      flex: flexByField.delete,
      minWidth: 70,
      sortable: false,
      renderHeader: () => <span />,
      renderCell: (params: any) => (
        <span className="u-row-action">
          <IconButton
            onClick={() => deleteRow(params.id)}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </span>
      ),
    },
  ], [expenses, totalPercentUsed, variables, modelDetails, operatingExpenses, expenseType, editingNameRowId, editingNameValue, showBuildableSQFt, buildableSQFt]);

  const columns = useMemo<GridColDef<Expense>[]>(() => {
    if (showMonths) return allColumns;
    return allColumns.filter(c => c.field !== 'start_month' && c.field !== 'end_month');
  }, [allColumns, showMonths]);

  const processRowUpdate = (newRow: Expense, oldRow: Expense) => {
    let updatedRow = { ...newRow };

    if (newRow.factor !== oldRow.factor) {
      // If factor changed, reset cost_per and statistic
      if (newRow.factor === "Total") {
        updatedRow.cost_per = 0;
        updatedRow.statistic = null;
      } else if (newRow.factor === "Total percent of other expenses") {
        // For "Total percent of other expenses", cost_per should be sum of other expenses
        const { id, type } = newRow;
        const otherTotals = expenses
          .filter((exp) => exp.type === type && exp.id !== id)
          .map((exp) => {
            if (exp.factor === "Total") return exp.cost_per;
            if (
              exp.factor && exp.factor.toLowerCase() === "per Unit".toLowerCase() ||
              exp.factor && exp.factor.toLowerCase() === "per SF".toLowerCase() ||
              exp.factor && exp.factor.toLowerCase() === "per Month".toLowerCase()
            ) {
              return Number(exp.cost_per) * Number(exp.statistic);
            }
            return 0;
          })
          .reduce((sum, val) => (sum ?? 0) + Number(val), 0);
        updatedRow.cost_per = otherTotals ?? 0;
        updatedRow.statistic = 0; // percentage starts at 0
      } else {
        updatedRow.cost_per = 0;
        updatedRow.statistic = 0;
      }
    }

    setExpenses((prev) =>
      prev.map((row) => (row.id === updatedRow.id ? updatedRow : row))
    );
    return updatedRow;
  };

  // Guards rapid duplicate adds from Autocomplete + button on same user gesture.
  const commitGuardRef = useRef(new CommitGuard());

  const addRow = () => {
    const newRow: Expense = {
      id: generateRowId(),
      name: "",
      factor: "Total", // default to "Total"
      cost_per: 0,
      statistic: null,
      type: expenseType,
      start_month: 0,
      end_month: 0,
    };
    setExpenses((prev) => [...prev, newRow]);
  };
  const addRowWithName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (commitGuardRef.current.shouldSkip(trimmed)) return;
    const newRow: Expense = {
      id: generateRowId(),
      name: trimmed,
      factor: "Total",
      cost_per: 0,
      statistic: null,
      type: expenseType,
      start_month: 0,
      end_month: 0,
    };
    setExpenses((prev) => [...prev, newRow]);
  };

  const deleteRow = (id: GridRowId) => {
    try {
      if (typeof window !== 'undefined' && (document as any)?.activeElement) {
        (document as any).activeElement?.blur?.();
      }
    } catch {}
    requestAnimationFrame(() => {
    setExpenses((prev) => prev.filter((row) => row.id !== id));
    });
  };

  const getRowClassName = (params: any) =>
    params.indexRelativeToCurrentPage % 2 === 0 ? 'u-row-odd' : 'u-row-even';

  // --- Mobile (card) view -------------------------------------------------
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [editId, setEditId] = useState<string | null>(null);
  const editingRow = editId ? expenses.find((r) => r.id === editId) ?? null : null;

  // Mirrors the Factor dropdown options built in the desktop grid cell.
  const getFactorOptions = (row: Expense): { value: string; label: string }[] => {
    let options = [
      { value: 'Total', label: 'Total' },
      { value: 'per Unit', label: 'per Unit' },
      { value: 'per SF', label: 'per SF' },
      { value: 'per Month', label: 'per Month' },
    ];
    const thisType = (row as any).type as string;
    if (isDevelopmentModel) {
      if (thisType === 'Closing Costs') {
        options = [
          { value: 'Percent of Purchase Price', label: 'Percent of Purchase Price' },
          { value: 'Percent of Construction Loan', label: 'Percent of Construction Loan' },
          { value: 'Percent of Pref / Mezz Loan', label: 'Percent of Pref / Mezz Loan' },
          { value: 'per Unit', label: 'per Unit' },
          { value: 'per SF', label: 'per SF' },
          { value: 'Total', label: 'Total' },
        ];
      } else if (thisType === 'Legal and Pre-Development Costs') {
        options = [
          { value: 'Percent of Construction Loan', label: 'Percent of Construction Loan' },
          { value: 'Percent of Pref / Mezz Loan', label: 'Percent of Pref / Mezz Loan' },
          { value: 'per Unit', label: 'per Unit' },
          { value: 'per SF', label: 'per SF' },
          { value: 'per Month', label: 'per Month' },
          { value: 'Total', label: 'Total' },
        ];
      } else if (thisType === 'Soft Costs') {
        options = [
          { value: 'Percent of Hard Costs', label: 'Percent of Hard Costs' },
          { value: 'per Unit', label: 'per Unit' },
          { value: 'per SF', label: 'per SF' },
          { value: 'per Month', label: 'per Month' },
          { value: 'Total', label: 'Total' },
        ];
      } else if (thisType === 'Hard Costs') {
        options = [
          { value: 'per Unit', label: 'per Unit' },
          { value: '$ / buildable SF', label: '$ / buildable SF' },
          { value: 'per SF', label: 'per SF' },
          { value: 'per Month', label: 'per Month' },
          { value: 'Total', label: 'Total' },
        ];
      }
    } else {
      if (thisType === 'Closing Costs') {
        options.push({ value: 'Percent of Purchase Price', label: 'Percent of Purchase Price' });
        options.push({ value: 'Percent of Acquisition Loan', label: 'Percent of Acquisition Loan' });
        if (!industrialModel) {
          options.push({ value: 'Percent of Property Taxes', label: 'Percent of Property Taxes' });
          options.push({ value: 'Percent of Insurance Cost', label: 'Percent of Insurance Cost' });
        }
      }
      if (thisType === 'Hard Costs' && showBuildableSQFt) {
        options.push({ value: '$ / buildable SF', label: '$ / buildable SF' });
      }
    }
    const rowsOfThisType = expenses.filter((exp) => exp.type === thisType);
    if (!rowsOfThisType.some((exp) => exp.factor === 'Total percent of other expenses' && exp.id !== row.id)) {
      options.push({ value: 'Total percent of other expenses', label: 'Percent of other expense total' });
    }
    return options;
  };

  // Mirrors the Cost Per adornments in the desktop grid cell.
  const getCostPerAdornment = (factor: string | null | undefined): { pre: string; suf: string } => {
    switch ((factor || '').toLowerCase()) {
      case 'per unit': return { pre: '$', suf: '/ unit' };
      case 'per sf': return { pre: '$', suf: '/ sf' };
      case '$ / buildable sf': return { pre: '$', suf: '/ buildable sf' };
      case 'per month': return { pre: '$', suf: '/ month' };
      case 'total': return { pre: '$', suf: '' };
      case 'total percent of other expenses':
      case 'percent of purchase price':
      case 'percent of insurance cost':
      case 'percent of property taxes':
      case 'percent of acquisition loan':
      case 'percent of construction loan':
      case 'percent of pref / mezz loan':
      case 'percent of hard costs':
        return { pre: '', suf: '%' };
      default: return { pre: '', suf: '' };
    }
  };

  const statLabelFor = (factor: string | null | undefined) => {
    switch ((factor || '').toLowerCase()) {
      case 'per unit': return 'Unit Count';
      case 'per sf': return 'Square Feet';
      case 'per month': return 'Months';
      default: return 'Statistic';
    }
  };

  if (isMobile) {
    const footer = (
      <CustomFooter
        expenses={expenses}
        expenseType={expenseType}
        modelDetails={modelDetails}
        variables={variables}
        operatingExpenses={operatingExpenses}
        units={units}
        amenityIncome={amenityIncome}
        retailIncome={retailIncome}
        retailExpenses={retailExpenses}
        onAdd={addRow}
        onAddWithName={addRowWithName}
        showMonths={showMonths}
        setShowMonths={setShowMonths}
        buildableSqFt={buildableSQFt}
        showBuildableSQFt={showBuildableSQFt}
        isDevelopmentModel={isDevelopmentModel}
      />
    );
    const editAdorn = editingRow ? getCostPerAdornment(editingRow.factor) : { pre: '', suf: '' };
    const editStatEditable = editingRow ? isCellEditable('statistic', editingRow) : false;
    return (
      <Box sx={{ mt: 0 }}>
        <Paper variant="outlined" sx={{ p: 0, mb: 2, borderRadius: 2, borderColor: colors.grey[300], overflow: 'hidden', background: colors.white }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5 }}>
            {orderedFilteredExpenses.length === 0 && (
              <Typography sx={{ color: colors.grey[600], fontSize: 14, textAlign: 'center', py: 2 }}>
                No items yet — add one below.
              </Typography>
            )}
            {orderedFilteredExpenses.map((row) => {
              const { pre, suf } = getCostPerAdornment(row.factor);
              const valueDisplay = `${pre}${Number(row.cost_per || 0).toLocaleString()}${suf ? ` ${suf}` : ''}`;
              const period = showMonths ? ` · Mo ${row.start_month || 0}-${row.end_month || 0}` : '';
              return (
                <Box key={row.id} sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 2, px: 1.5, py: 1.25, bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: colors.grey[900], fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.name || 'Untitled'}
                    </Typography>
                    <Typography sx={{ color: colors.grey[600], fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.factor} · {valueDisplay}{period}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => setEditId(row.id)} sx={{ p: 0.5 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton size="small" color="error" onClick={() => deleteRow(row.id)} sx={{ p: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {footer}
        </Paper>

        <Dialog open={!!editingRow} onClose={() => setEditId(null)} fullWidth maxWidth="xs">
          <DialogTitle>Edit {editingRow?.name || 'Item'}</DialogTitle>
          <DialogContent>
            {editingRow && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="Name" size="small" fullWidth value={editingRow.name ?? ''}
                  onChange={(e) => handleCellChange(editingRow.id, 'name', e.target.value)} />
                <TextField select label="Factor" size="small" fullWidth value={editingRow.factor ?? ''}
                  onChange={(e) => handleCellChange(editingRow.id, 'factor', e.target.value)}>
                  {getFactorOptions(editingRow).map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </TextField>
                <MobileNumberField label="Cost Per" type="number" size="small" fullWidth value={editingRow.cost_per ?? ''}
                  onChange={(e) => handleCellChange(editingRow.id, 'cost_per', Number(e.target.value))}
                  InputProps={{
                    ...(editAdorn.pre ? { startAdornment: <InputAdornment position="start">{editAdorn.pre}</InputAdornment> } : {}),
                    ...(editAdorn.suf ? { endAdornment: <InputAdornment position="end">{editAdorn.suf}</InputAdornment> } : {}),
                  }} />
                {editStatEditable && (
                  <MobileNumberField label={statLabelFor(editingRow.factor)} type="number" size="small" fullWidth value={editingRow.statistic ?? ''}
                    onChange={(e) => handleCellChange(editingRow.id, 'statistic', Number(e.target.value))} />
                )}
                {showMonths && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <MobileNumberField label="Start Month" type="number" size="small" fullWidth value={editingRow.start_month ?? ''}
                      onChange={(e) => handleCellChange(editingRow.id, 'start_month', Number(e.target.value))} />
                    <MobileNumberField label="End Month" type="number" size="small" fullWidth value={editingRow.end_month ?? ''}
                      onChange={(e) => handleCellChange(editingRow.id, 'end_month', Number(e.target.value))} />
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditId(null)} variant="contained">Done</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // The grid used to have no height of its own, so its outer height was
  // content-driven — and that closes a feedback loop: a horizontal scrollbar
  // appearing adds height, which can bring in the page's vertical scrollbar,
  // which narrows the container, which re-solves the flex columns, which
  // changes whether the scrollbars are needed at all. Toggling the month
  // columns perturbs the widths enough to start it, and MUI's resize-observer
  // throttle paces the oscillation at about a second.
  //
  // Deriving the height from the row COUNT breaks the cycle: it is a number
  // rather than a measurement, so scrollbars can no longer change the height
  // that decides whether scrollbars are needed.
  const expenseGridHeight = Math.min(
    MAX_EXPENSE_GRID_HEIGHT,
    EXPENSE_GRID_CHROME_HEIGHT + Math.max(1, orderedFilteredExpenses.length) * EXPENSE_ROW_HEIGHT
  );

  return (
    <Box sx={{ mt: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, minWidth: '900px' }} />
      <Paper variant="outlined" sx={{ p: 0, mb: 2, borderRadius: 2, borderColor: colors.grey[300], overflow: 'hidden', background: colors.white }}>
        <DataGrid
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableColumnSorting
          rows={orderedFilteredExpenses}
          columns={columns.map(col => ({ ...col, sortable: false }))}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          getRowId={(row) => row.id}
          isCellEditable={(params) => isCellEditable(params.field, params.row)}
          slots={{
            footer: () => (
              <CustomFooter
                expenses={expenses}
                expenseType={expenseType}
                modelDetails={modelDetails}
                variables={variables}
                operatingExpenses={operatingExpenses}
                units={units}
                amenityIncome={amenityIncome}
                retailIncome={retailIncome}
                retailExpenses={retailExpenses}
                onAdd={addRow}
                onAddWithName={addRowWithName}
                showMonths={showMonths}
                setShowMonths={setShowMonths}
                buildableSqFt={buildableSQFt}
                showBuildableSQFt={showBuildableSQFt}
                isDevelopmentModel={isDevelopmentModel}
              />
            )
          }}
          rowHeight={EXPENSE_ROW_HEIGHT}
          getRowClassName={getRowClassName}
          hideFooterSelectedRowCount
          sx={{
            height: expenseGridHeight,
            background: colors.white,
            minWidth: '900px',
            fontFamily: typography.fontFamily,
            fontSize: 14.5,
            '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cellContent': {
              fontFamily: typography.fontFamily,
              fontSize: 14.5,
            },
            '& .MuiDataGrid-main': { background: colors.white },
            '& .MuiDataGrid-columnHeaders': { background: colors.white, minHeight: 50, maxHeight: 50, borderBottom: `1px solid ${colors.grey[300]}` },
            '& .MuiDataGrid-columnHeader': { background: colors.white, minHeight: 50, maxHeight: 50 },
            '& .MuiDataGrid-columnHeaderTitleContainer': { background: colors.white },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: 14.5,
              fontFamily: typography.fontFamily,
              textTransform: 'none',
              lineHeight: '50px',
              color: colors.grey[900],
            },
            '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[300]}`, background: colors.white, fontSize: 14.5, color: colors.grey[900] },
            '& .MuiDataGrid-row': { background: colors.white },
            '& .u-row-even': { background: colors.grey[50] },
            '& .MuiDataGrid-row:hover': { backgroundColor: colors.blueTint },
            '& .u-editable-input': { border: 'none', borderBottom: '2px solid transparent', borderRadius: 0, background: 'transparent', fontWeight: 600 },
            '& .u-editable-input input': { fontWeight: 600 },
            '& .u-select': { fontFamily: typography.fontFamily, fontSize: 14.5, fontWeight: 600, color: colors.grey[900] },
            '& .MuiDataGrid-row:hover .u-editable-input, & .u-editable-input:focus': { borderBottom: `2px solid ${colors.blue} !important` },
            '& .u-row-action': {
              opacity: 0,
              visibility: 'hidden',
              pointerEvents: 'none',
              transition: 'opacity 120ms ease'
            },
            '& .MuiDataGrid-row:hover .u-row-action, & .MuiDataGrid-cell:focus-within .u-row-action': {
              opacity: 1,
              visibility: 'visible',
              pointerEvents: 'auto'
            },
            '& .u-caret': { opacity: 0, transition: 'opacity 120ms ease', color: colors.blue },
            '& .u-select:focus + .u-caret': { opacity: 1 },
            '& .MuiDataGrid-row:hover .u-caret': { opacity: 1 },
            '& .MuiDataGrid-virtualScroller': { background: colors.white },
            '& .MuiDataGrid-footerContainer': { background: colors.white }
          }}
        />
      </Paper>
    </Box>
  );
};
